/**
 * Инициализация Telegram SDK.
 *
 * Вызывается один раз до отрисовки: компонентам нужны готовые параметры
 * темы и размеры области.
 */
import {
  backButton,
  init as initSdk,
  miniApp,
  retrieveLaunchParams,
  swipeBehavior,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react';

// isDarkTheme() и platform() здесь были, пока оформление приходило из
// telegram-ui: ему передавались тема и платформа. Палитра приложения теперь
// своя и фиксированно светлая, а различий по платформам в вёрстке нет,
// поэтому обе функции удалены вместе с библиотекой.

/**
 * Развернуть приложение на весь экран.
 *
 * Полноэкранный режим появился в Mini Apps 8.0 и есть не везде: на десктопе
 * и в старых клиентах метода просто нет. Отказ здесь — обычное положение
 * дел, а не сбой, поэтому он гасится: приложение работает и в обычном окне.
 */
async function enterFullscreen(): Promise<void> {
  if (viewport.isFullscreen() || !viewport.requestFullscreen.isAvailable()) {
    return;
  }
  try {
    await viewport.requestFullscreen();
  } catch {
    // Клиент может отказать — например, при открытии в режиме,
    // где полный экран запрещён. Для приложения это не критично.
  }
}

/**
 * Отключить вертикальный свайп-жест клиента.
 *
 * Иначе протяжка вниз на странице воспринимается как жест закрытия и тянет
 * за собой всё окно приложения вместо прокрутки содержимого. Доступно
 * с Mini Apps 7.7.
 */
function lockVerticalSwipe(): void {
  if (!swipeBehavior.mount.isAvailable()) {
    return;
  }
  swipeBehavior.mount();
  if (swipeBehavior.disableVertical.isAvailable()) {
    swipeBehavior.disableVertical();
  }
}

/**
 * Подготовить окружение.
 *
 * Возвращает признак «запущено внутри Telegram»: вне мессенджера
 * приложение бессмысленно, но падать с белым экраном оно не должно —
 * человек может открыть ссылку в обычном браузере.
 */
export function setupTelegram(): boolean {
  try {
    initSdk();
  } catch {
    return false;
  }

  try {
    retrieveLaunchParams();
  } catch {
    return false;
  }

  if (themeParams.mountSync.isAvailable()) {
    themeParams.mountSync();
    themeParams.bindCssVars();
  }
  if (miniApp.mountSync.isAvailable()) {
    miniApp.mountSync();
    miniApp.bindCssVars();
  }

  lockVerticalSwipe();

  if (viewport.mount.isAvailable()) {
    // Полный экран запрашивается только после монтирования viewport:
    // до него у компонента нет состояния, и вызов отклоняется.
    void viewport.mount().then(() => {
      if (viewport.bindCssVars.isAvailable()) {
        viewport.bindCssVars();
      }
      void enterFullscreen();
    });
  }

  if (backButton.mount.isAvailable()) {
    backButton.mount();
  }
  if (miniApp.ready.isAvailable()) {
    miniApp.ready();
  }
  return true;
}
